import { action, observable } from 'mobx';
import { Container, Inject } from 'typescript-ioc';
import { IntlShape } from 'react-intl';
import {
    HubConfigurationSettingsType,
    IEventHubsDAO,
    IHubSignInDTO,
} from '../../../../../../../api/event-hubs.dao';
import { EventHubsDAO } from '../../../../../../../api/implementations/event-hubs.dao';
import { IReactIntlProvider } from '../../../../../../../infrastructure/react-intl-provider';
import { IErrorParsing } from '../../../../../../../infrastructure/error-parsing';
import { alertServerError } from '../../../../../../global/modal/alerts-host/alert-templates';
import { LONG_RICH_TEXT_LENGTH, SHORT_PLAIN_TEXT_LENGTH } from '../../../../../../../constants/common.constants';
import {
    MAX_LENGTH_NOT_REQUIRED_ERROR_MESSAGE,
    MAX_LONG_RICH_TEXT_LENGTH_ERROR_MESSAGE,
    REQUIRED_ERROR_MESSAGE,
} from '../../../../../../../constants/validation-errors';
import { calcByteCodeSize } from '../../../../../../../infrastructure/utils/common.utils';
import { EVENT_HUB_TRACKING_EVENTS } from '../../../../../../../tracking-service/tracking-values';
import TrackingServiceProvider from '../../../../../../../tracking-service/tracking-service-provider';
import {
    transpileClassesToInlineStyles,
    transpileInlineStylesToClasses,
} from '../../../../../../global/rich-text-editor/utils/transpiler';
import { HubPreviewRefreshCallback } from '../../../../_content/_hub-embed/hub-preview-sync.types';
import { buildSignInPreviewExpectation } from '../../../../_content/_hub-embed/hub-preview-sync.expectations';

export const DEFAULT_REQUEST_LINK_BUTTON_LABEL = 'Request Sign In Link';
export const DEFAULT_SIGN_IN_LINK_REQUESTED_MESSAGE =
    'Thank you. If {{Email_Address}} is registered, then we will send you an email with a sign in link. Please check your inbox and click the link to sign in.';

const isRichTextEmpty = (value: string): boolean =>
    !value.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();

export class HubSignInSettingsVM {
    @observable
    private oneStepSignInEnabled = false;
    @observable
    private displayName = '';
    @observable
    private requestLinkInstructions = '';
    @observable
    private requestLinkButtonLabel = DEFAULT_REQUEST_LINK_BUTTON_LABEL;
    @observable
    private signInLinkRequestedMessage = DEFAULT_SIGN_IN_LINK_REQUESTED_MESSAGE;
    @observable
    private requestLinkButtonLabelTouched = false;
    @observable
    private signInLinkRequestedMessageTouched = false;
    @observable
    private loading = false;
    @observable
    private saving = false;

    private oneStepSignInEnabledSnapshot = false;
    private displayNameSnapshot = '';
    private requestLinkInstructionsSnapshot = '';
    private requestLinkButtonLabelSnapshot = DEFAULT_REQUEST_LINK_BUTTON_LABEL;
    private signInLinkRequestedMessageSnapshot = DEFAULT_SIGN_IN_LINK_REQUESTED_MESSAGE;

    constructor(
        private readonly accountId: number,
        private readonly eventHubId: number,
        @Inject private readonly eventHubsDAO: IEventHubsDAO = Container.get(EventHubsDAO),
        @Inject private readonly intlProvider: IReactIntlProvider = Container.get(IReactIntlProvider),
        @Inject private readonly trackingService: TrackingServiceProvider = Container.get(TrackingServiceProvider),
        private readonly onHubPreviewRefresh?: HubPreviewRefreshCallback,
    ) {
        void this.loadSettings();
    }

    public get intl(): IntlShape {
        return this.intlProvider.getIntl();
    }

    public isLoading(): boolean {
        return this.loading;
    }

    public isSaving(): boolean {
        return this.saving;
    }

    public isOneStepSignInEnabled(): boolean {
        return this.oneStepSignInEnabled;
    }

    public getDisplayName(): string {
        return this.displayName;
    }

    public getRequestLinkInstructions(): string {
        return this.requestLinkInstructions;
    }

    public getRequestLinkButtonLabel(): string {
        return this.requestLinkButtonLabel;
    }

    public getSignInLinkRequestedMessage(): string {
        return this.signInLinkRequestedMessage;
    }

    public getDisplayNameError(): string | null {
        if (this.displayName.trim().length > SHORT_PLAIN_TEXT_LENGTH) {
            return MAX_LENGTH_NOT_REQUIRED_ERROR_MESSAGE;
        }
        return null;
    }

    public getRequestLinkInstructionsError(): string | null {
        if (calcByteCodeSize(this.requestLinkInstructions) > LONG_RICH_TEXT_LENGTH) {
            return MAX_LONG_RICH_TEXT_LENGTH_ERROR_MESSAGE;
        }
        return null;
    }

    public getRequestLinkButtonLabelError(): string | null {
        const trimmedButtonLabel = this.requestLinkButtonLabel.trim();
        if (!trimmedButtonLabel) {
            return this.requestLinkButtonLabelTouched ? REQUIRED_ERROR_MESSAGE : null;
        }
        if (trimmedButtonLabel.length > SHORT_PLAIN_TEXT_LENGTH) {
            return MAX_LENGTH_NOT_REQUIRED_ERROR_MESSAGE;
        }
        return null;
    }

    public getSignInLinkRequestedMessageError(): string | null {
        if (isRichTextEmpty(this.signInLinkRequestedMessage)) {
            return this.signInLinkRequestedMessageTouched ? REQUIRED_ERROR_MESSAGE : null;
        }
        if (calcByteCodeSize(this.signInLinkRequestedMessage) > LONG_RICH_TEXT_LENGTH) {
            return MAX_LONG_RICH_TEXT_LENGTH_ERROR_MESSAGE;
        }
        return null;
    }

    public hasUnsavedChanges(): boolean {
        return this.oneStepSignInEnabledSnapshot !== this.oneStepSignInEnabled
            || this.displayNameSnapshot !== this.displayName
            || this.requestLinkInstructionsSnapshot !== this.requestLinkInstructions
            || this.requestLinkButtonLabelSnapshot !== this.requestLinkButtonLabel
            || this.signInLinkRequestedMessageSnapshot !== this.signInLinkRequestedMessage;
    }

    public isSaveButtonDisabled(): boolean {
        return this.loading || this.saving;
    }

    public isCancelButtonDisabled(): boolean {
        return this.loading || this.saving || !this.hasUnsavedChanges();
    }

    @action
    public toggleOneStepSignInEnabled = (): void => {
        this.oneStepSignInEnabled = !this.oneStepSignInEnabled;
    };

    @action
    public setDisplayName = (value: string): void => {
        this.displayName = value;
    };

    @action
    public setRequestLinkInstructions = (value: string): void => {
        this.requestLinkInstructions = value;
    };

    @action
    public setRequestLinkButtonLabel = (value: string): void => {
        this.requestLinkButtonLabel = value;
    };

    @action
    public setSignInLinkRequestedMessage = (value: string): void => {
        this.signInLinkRequestedMessage = value;
    };

    @action
    public markRequestLinkButtonLabelTouched = (): void => {
        this.requestLinkButtonLabelTouched = true;
    };

    @action
    public markSignInLinkRequestedMessageTouched = (): void => {
        this.signInLinkRequestedMessageTouched = true;
    };

    @action
    public onCancel = (): void => {
        this.oneStepSignInEnabled = this.oneStepSignInEnabledSnapshot;
        this.displayName = this.displayNameSnapshot;
        this.requestLinkInstructions = this.requestLinkInstructionsSnapshot;
        this.requestLinkButtonLabel = this.requestLinkButtonLabelSnapshot;
        this.signInLinkRequestedMessage = this.signInLinkRequestedMessageSnapshot;
        this.requestLinkButtonLabelTouched = false;
        this.signInLinkRequestedMessageTouched = false;
    };

    public onSaveChanges = (): void => {
        void this.saveSettings();
    };

    @action
    private setLoading = (loading: boolean): void => {
        this.loading = loading;
    };

    @action
    private setSaving = (saving: boolean): void => {
        this.saving = saving;
    };

    @action
    private applyLoadedState = (signInSettings: IHubSignInDTO | undefined): void => {
        const oneStepSignInEnabled = signInSettings?.oneStepSignInEnabled ?? false;
        const displayName = signInSettings?.signInDisplayName ?? '';
        const requestLinkInstructions = transpileInlineStylesToClasses(
            signInSettings?.requestLinkInstructions ?? ''
        );
        const requestLinkButtonLabel = signInSettings?.requestLinkButtonLabel
            || DEFAULT_REQUEST_LINK_BUTTON_LABEL;
        const signInLinkRequestedMessage = transpileInlineStylesToClasses(
            signInSettings?.signInLinkRequestedMessage || DEFAULT_SIGN_IN_LINK_REQUESTED_MESSAGE
        );

        this.oneStepSignInEnabled = oneStepSignInEnabled;
        this.displayName = displayName;
        this.requestLinkInstructions = requestLinkInstructions;
        this.requestLinkButtonLabel = requestLinkButtonLabel;
        this.signInLinkRequestedMessage = signInLinkRequestedMessage;
        this.requestLinkButtonLabelTouched = false;
        this.signInLinkRequestedMessageTouched = false;

        this.oneStepSignInEnabledSnapshot = oneStepSignInEnabled;
        this.displayNameSnapshot = displayName;
        this.requestLinkInstructionsSnapshot = requestLinkInstructions;
        this.requestLinkButtonLabelSnapshot = requestLinkButtonLabel;
        this.signInLinkRequestedMessageSnapshot = signInLinkRequestedMessage;
    };

    @action
    private commitSavedState = (): void => {
        this.requestLinkButtonLabelTouched = false;
        this.signInLinkRequestedMessageTouched = false;
        this.oneStepSignInEnabledSnapshot = this.oneStepSignInEnabled;
        this.displayNameSnapshot = this.displayName;
        this.requestLinkInstructionsSnapshot = this.requestLinkInstructions;
        this.requestLinkButtonLabelSnapshot = this.requestLinkButtonLabel;
        this.signInLinkRequestedMessageSnapshot = this.signInLinkRequestedMessage;
    };

    private async loadSettings(showLoading = true): Promise<void> {
        if (showLoading) {
            this.setLoading(true);
        }
        try {
            const configs = await this.eventHubsDAO.getEventHubConfigs(this.accountId, this.eventHubId);
            const signInSettings = configs.find(
                config => config.type === HubConfigurationSettingsType.SIGN_IN
            ) as IHubSignInDTO | undefined;

            this.applyLoadedState(signInSettings);
        } catch (error) {
            this.handleHubApiException(error);
        } finally {
            if (showLoading) {
                this.setLoading(false);
            }
        }
    }

    private async saveSettings(): Promise<void> {
        if (this.loading || this.saving || !this.hasUnsavedChanges()) {
            return;
        }

        this.markRequestLinkButtonLabelTouched();
        this.markSignInLinkRequestedMessageTouched();
        if (
            this.getDisplayNameError()
            || this.getRequestLinkInstructionsError()
            || this.getRequestLinkButtonLabelError()
            || this.getSignInLinkRequestedMessageError()
        ) {
            return;
        }

        this.setSaving(true);
        const refreshGeneration = this.onHubPreviewRefresh?.beginLoading();
        try {
            const signInPayload: IHubSignInDTO = {
                accountId: this.accountId,
                eventHubId: this.eventHubId,
                type: HubConfigurationSettingsType.SIGN_IN,
                oneStepSignInEnabled: this.oneStepSignInEnabled,
                signInDisplayName: this.displayName.trim() || null,
                requestLinkInstructions: isRichTextEmpty(this.requestLinkInstructions)
                    ? null
                    : transpileClassesToInlineStyles(this.requestLinkInstructions),
                requestLinkButtonLabel: this.requestLinkButtonLabel.trim(),
                signInLinkRequestedMessage: transpileClassesToInlineStyles(this.signInLinkRequestedMessage),
            };

            await this.eventHubsDAO.setEventHubConfig(signInPayload);
            this.commitSavedState();
            this.trackingService.get().track(EVENT_HUB_TRACKING_EVENTS.CONFIGURE_HUB_SIGN_IN, {});
            void this.onHubPreviewRefresh?.refresh(
                buildSignInPreviewExpectation(signInPayload),
                refreshGeneration,
            );
        } catch (error) {
            if (refreshGeneration != null) {
                this.onHubPreviewRefresh?.cancelLoading(refreshGeneration);
            }
            this.handleHubApiException(error);
            await this.loadSettings(false);
        } finally {
            this.setSaving(false);
        }
    }

    private handleHubApiException(error: Error): void {
        const errorParsingService = Container.get(IErrorParsing);
        const apiError = errorParsingService.extractAPIError(error);
        const title = apiError?.errorCode === 403 ? 'Access Denied' : 'Error';
        alertServerError(title, errorParsingService.extractMessage(error));
    }
}
